import * as errorTypes from '../constants/errorsTypes';
import * as widgets from '@mm/constants/widgets';
import * as fields from '../../../constants/fields';
import WidgetModel, {
  FollowUpType,
  ValidationMessage,
} from '@mm/state/model/WidgetModel';

const getWithUpperCasedFirst = (str: string) =>
  `${str.slice(0, 1).toUpperCase()}${str.slice(1)}`;

const numberOfItems = (
  val: string | ValidationMessage[] | null | undefined,
) => {
  switch (val) {
    case '1':
      return 'one item';
    case '2':
      return 'two items';
    case '3':
      return 'three items';
    default:
      return `${val} items`;
  }
};

const errorByType = new Map([
  [errorTypes.MIN_LENGTH, (field: string) => `${field} cannot be empty`],
  [
    errorTypes.MIN_ITEMS,
    (
      field: string,
      model: string,
      value: string | ValidationMessage[] | null | undefined,
    ) => {
      if (model === widgets.QUICK_QUESTION) {
        return 'Multiple Choice question must have at least one answer';
      }

      if (model === widgets.FORM) {
        return 'Multi choice questions must have at least one option in Values';
      }

      return `${field} must contain at least ${numberOfItems(value)}`;
    },
  ],
  [
    errorTypes.MAX_ITEMS,
    (field: string, model: string, value: string) => {
      if (model === widgets.LIST) return `Too many ${model} buttons`;
      return `${field} cannot be more than ${value}`;
    },
  ],
  [
    errorTypes.TYPEV4,
    (field: string, model: string) => {
      if (model === widgets.NAVIGATE) return 'Must select page';
      return `${field} cannot be empty`;
    },
  ],
  [
    errorTypes.PATTERN,
    (field: string) => `${field} does not match the pattern`,
  ],
  [errorTypes.UNIQUE, (model, name, message: string) => message],
  [
    errorTypes.MAX_LENGTH,
    (field: string, name, value: string) =>
      `${getWithUpperCasedFirst(field)} should have less chars than ${value}`,
  ],
  [errorTypes.FB, (field, name, value: string) => value],
  [
    errorTypes.REQUIRED,
    (field: string, type: string) =>
      [widgets.SEND_DATA, widgets.OTN].indexOf(type) !== -1
        ? `${field} cannot be empty`
        : '',
  ],
  [
    errorTypes.BUTTON_DIALOGUE_DELETED,
    (field: string, name: string, value: string) => value,
  ],
  [
    errorTypes.WRONG_SETTINGS,
    (field: string, name: string, value?: any, widget?: WidgetModel) => {
      if (name === widgets.QUICK_QUESTION) {
        const config = widget?.config;
        switch (config?.followUpType?.toString()) {
          case FollowUpType.reminder: {
            if (!config?.followUpText) {
              return 'Widget follow-up reminder should not be empty';
            }
            break;
          }
          case FollowUpType.page: {
            if (!config?.followUpPageId) {
              return 'Follow-up dialogue should not be empty';
            }
            break;
          }
          default:
            return 'Wrong follow-up settings';
        }
      }
      return value;
    },
  ],
]);

const getMainFieldName = (model: string) => {
  switch (model) {
    case widgets.FORM:
      return 'Question or prompt';
    case widgets.QUICK_QUESTION:
      return 'Question';
    default:
      return getWithUpperCasedFirst(model);
  }
};

const getByName = new Map([
  [fields.NAME, 'Form Name'],
  [fields.TAG, '1-Time Notification Tag'],
  [fields.RECIPIENT, 'Recipient'],
  [fields.SUBJECT, 'Subject'],
  [fields.BOT_VARIABLE_ID, 'Attribute'],
  [fields.VALUE, 'Value'],
  [fields.ANSWER, 'Answer'],
  [fields.URL, 'URL'],
  [fields.MESSAGE, 'Message'],
  [fields.COMPANY_USER_IDS, 'Company Users to Notifiy'],
  [fields.BUTTONS, 'Button'],
  [fields.FOLLOW_UP, 'Follow Up'],
]);

const getByModel = new Map([
  [widgets.GALLERY, 'Gallery element'],
  [widgets.LIST, 'List element'],
  [widgets.QUICK_QUESTION, 'Question'],
  [widgets.SEND_DATA, 'Zap'],
]);

const getNameByModel = (model: string, errorType: string) => {
  switch (model) {
    case widgets.FORM:
      return errorTypes.MAX_LENGTH === errorType ? 'value' : 'name';
    case widgets.QUICK_QUESTION:
      return 'answer';
    default:
      return 'name';
  }
};

const getFieldName = (name: string, model: string, errorType: string) => {
  if (name === 'body') return getMainFieldName(model);
  const newName = name === 'name' ? getNameByModel(model, errorType) : name;
  return (
    getByName.get(newName) ||
    getByModel.get(model) ||
    getWithUpperCasedFirst(model)
  );
};

export const validationMessages = (
  {
    fieldName,
    errorType,
    value,
  }: {
    fieldName: string;
    errorType: string;
    value: string | ValidationMessage[] | null | undefined;
  },
  model: WidgetModel,
) => {
  const field = getFieldName(fieldName, model.type, errorType);
  const errorMessage = errorByType.get(errorType);

  if (errorMessage) {
    return errorMessage(field, model.type, value, model);
  }
  return '';
};
